import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Users, Search, Eye, LogIn, Shield, Stethoscope, GraduationCap, MapPin, ArrowLeft, Headphones, Monitor } from 'lucide-react';
import SiteHeader from '@/components/SiteHeader';
import Footer from '@/components/Footer';
import { API } from '@/App';
import { refreshFileSession } from '@/lib/fileSession';

const ROLE_CONFIG = {
  super_admin: { label: 'Admin', color: '#1e3a5f', bg: '#e8edf2', icon: Shield },
  ophthalmologist: { label: 'Ophtalmo', color: '#35AFE4', bg: '#eaf6fc', icon: Stethoscope },
  regional: { label: 'Régional', color: '#d97706', bg: '#fef3c7', icon: MapPin },
  intern: { label: 'Interne', color: '#059669', bg: '#d1fae5', icon: GraduationCap },
  tresorier: { label: 'Trésorier', color: '#7c3aed', bg: '#ede9fe', icon: Shield },
  auteur: { label: 'Auteur', color: '#ec4899', bg: '#fce7f3', icon: Shield },
};

const VIEW_MODES = [
  { id: 'users', label: 'Utilisateurs', icon: Users },
  { id: 'tickets', label: 'Tickets Support', icon: Headphones },
  { id: 'views', label: 'Aperçu des vues', icon: Monitor },
];

const SupportTechPage = () => {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [activeView, setActiveView] = useState('users');
  const token = localStorage.getItem('token');
  const currentUser = JSON.parse(localStorage.getItem('user') || 'null');
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    if (!currentUser?.is_support) { navigate('/'); return; }
    Promise.all([
      axios.get(`${API}/admin/users`, { headers }).then(r => setUsers(r.data)),
      axios.get(`${API}/tickets`, { headers }).then(r => setTickets(r.data)),
    ]).finally(() => setLoading(false));
  }, []);

  const impersonate = async (userId) => {
    try {
      const r = await axios.post(`${API}/auth/impersonate/${userId}`, {}, { headers });
      // Store original support session
      localStorage.setItem('support_token', token);
      localStorage.setItem('support_user', JSON.stringify(currentUser));
      // Switch to impersonated user
      localStorage.setItem('token', r.data.token);
      localStorage.setItem('user', JSON.stringify(r.data.user));
      localStorage.setItem('impersonating', 'true');
      await refreshFileSession();
      window.location.href = '/dashboard';
    } catch (e) {
      alert(e.response?.data?.detail || 'Erreur');
    }
  };

  const filteredUsers = users.filter(u => {
    if (search) {
      const q = search.toLowerCase();
      if (!(`${u.first_name} ${u.last_name} ${u.email}`.toLowerCase().includes(q))) return false;
    }
    if (roleFilter && u.role !== roleFilter) return false;
    return true;
  });

  const roleCounts = {};
  users.forEach(u => { roleCounts[u.role] = (roleCounts[u.role] || 0) + 1; });

  if (loading) return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center">
      <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#35AFE4]" />
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50" style={{ fontFamily: "'Roboto', sans-serif" }}>
      <SiteHeader />
      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-orange-100 flex items-center justify-center">
              <Headphones size={20} className="text-orange-600" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-gray-900" style={{ fontFamily: "'Barlow', sans-serif" }}>Support Technique</h1>
              <p className="text-xs text-gray-400">{users.length} utilisateurs | {tickets.length} tickets</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => navigate('/support-tech/audit')}
              className="hidden sm:flex items-center gap-2 px-4 py-2 text-sm text-[#1e3a5f] hover:text-white hover:bg-[#1e3a5f] bg-white border border-[#1e3a5f]/20 rounded-lg transition-colors font-semibold"
              data-testid="audit-link">
              <Shield size={14} /> Audit
            </button>
            <button onClick={() => navigate('/admin')} className="flex items-center gap-2 px-4 py-2 text-sm text-gray-500 hover:text-gray-700 bg-white border border-gray-200 rounded-lg">
              <ArrowLeft size={14} /> Back Office
            </button>
          </div>
        </div>

        {/* View tabs */}
        <div className="flex gap-1 mb-6 bg-white rounded-xl p-1 border border-gray-100 w-fit">
          {VIEW_MODES.map(v => (
            <button key={v.id} onClick={() => setActiveView(v.id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${activeView === v.id ? 'bg-[#1e3a5f] text-white shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
              data-testid={`support-tab-${v.id}`}>
              <v.icon size={14} /> {v.label}
            </button>
          ))}
        </div>

        {/* === USERS VIEW === */}
        {activeView === 'users' && (
          <div>
            {/* Role filter pills */}
            <div className="flex flex-wrap gap-2 mb-4">
              <button onClick={() => setRoleFilter('')}
                className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${!roleFilter ? 'bg-[#1e3a5f] text-white' : 'bg-white text-gray-500 border border-gray-200 hover:bg-gray-50'}`}>
                Tous ({users.length})
              </button>
              {Object.entries(ROLE_CONFIG).map(([role, cfg]) => {
                const count = roleCounts[role] || 0;
                if (!count) return null;
                const Icon = cfg.icon;
                return (
                  <button key={role} onClick={() => setRoleFilter(roleFilter === role ? '' : role)}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-all ${roleFilter === role ? 'text-white shadow-sm' : 'bg-white border border-gray-200 hover:bg-gray-50'}`}
                    style={roleFilter === role ? { background: cfg.color } : { color: cfg.color }}>
                    <Icon size={11} /> {cfg.label} ({count})
                  </button>
                );
              })}
            </div>

            {/* Search */}
            <div className="relative mb-4">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Rechercher un membre..."
                className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-[#35AFE4]" data-testid="support-search" />
            </div>

            {/* User list */}
            <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr className="text-[10px] text-gray-400 uppercase tracking-wider">
                    <th className="px-4 py-3 text-left">Membre</th>
                    <th className="px-4 py-3 text-left">Email</th>
                    <th className="px-4 py-3 text-left">Role</th>
                    <th className="px-4 py-3 text-left">Statut</th>
                    <th className="px-4 py-3 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {filteredUsers.slice(0, 50).map(u => {
                    const cfg = ROLE_CONFIG[u.role] || ROLE_CONFIG.ophthalmologist;
                    const Icon = cfg.icon;
                    return (
                      <tr key={u.id} className="hover:bg-gray-50/50" data-testid={`support-user-${u.id}`}>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2.5">
                            {u.profile_image ? (
                              <img src={u.profile_image.startsWith('/') ? `${API.replace('/api','')}${u.profile_image}` : u.profile_image} alt="" className="w-8 h-8 rounded-full object-cover" />
                            ) : (
                              <div className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold text-white" style={{ background: cfg.color }}>
                                {(u.first_name?.[0] || '')}{(u.last_name?.[0] || '')}
                              </div>
                            )}
                            <span className="text-sm font-medium text-gray-900">{u.first_name} {u.last_name}</span>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-xs text-gray-500">{u.email}</td>
                        <td className="px-4 py-3">
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold" style={{ background: cfg.bg, color: cfg.color }}>
                            <Icon size={10} /> {cfg.label}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <span className={`inline-block w-2 h-2 rounded-full ${u.subscription_status === 'active' ? 'bg-green-500' : 'bg-gray-300'}`} />
                        </td>
                        <td className="px-4 py-3 text-right">
                          <div className="flex items-center gap-1 justify-end">
                            <button onClick={() => impersonate(u.id)} title="Se connecter en tant que"
                              className="p-1.5 rounded-lg text-gray-400 hover:text-[#35AFE4] hover:bg-[#eaf6fc] transition-colors" data-testid={`impersonate-${u.id}`}>
                              <LogIn size={14} />
                            </button>
                            <button onClick={() => navigate(`/admin/members/${u.id}`)} title="Voir profil"
                              className="p-1.5 rounded-lg text-gray-400 hover:text-[#1e3a5f] hover:bg-gray-100 transition-colors">
                              <Eye size={14} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              {filteredUsers.length === 0 && (
                <div className="text-center py-8 text-gray-400 text-sm">Aucun utilisateur trouvé</div>
              )}
            </div>
          </div>
        )}

        {/* === TICKETS VIEW === */}
        {activeView === 'tickets' && (
          <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
            <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
              <h2 className="font-bold text-gray-900">Tous les tickets</h2>
              <span className="text-xs text-gray-400">{tickets.length} ticket(s)</span>
            </div>
            {tickets.length === 0 ? (
              <div className="text-center py-12 text-gray-400 text-sm">Aucun ticket</div>
            ) : (
              <div className="divide-y divide-gray-50">
                {tickets.map(t => (
                  <div key={t.id} className="px-5 py-4 hover:bg-gray-50/50 flex items-center gap-4" data-testid={`ticket-${t.id}`}>
                    <div className={`w-2 h-2 rounded-full flex-shrink-0 ${t.status === 'open' ? 'bg-red-500' : t.status === 'in_progress' ? 'bg-amber-500' : 'bg-green-500'}`} />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">{t.subject}</p>
                      <p className="text-xs text-gray-400">{t.reporter_name} | {new Date(t.created_at).toLocaleDateString('fr-FR')}</p>
                    </div>
                    <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${t.status === 'open' ? 'bg-red-100 text-red-600' : t.status === 'in_progress' ? 'bg-amber-100 text-amber-600' : 'bg-green-100 text-green-600'}`}>
                      {t.status === 'open' ? 'Ouvert' : t.status === 'in_progress' ? 'En cours' : 'Résolu'}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* === VIEWS PREVIEW === */}
        {activeView === 'views' && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {[
              { label: 'Page d\'accueil', path: '/', desc: 'Vue publique' },
              { label: 'Annuaire', path: '/annuaire', desc: 'Annuaire des praticiens' },
              { label: 'Formations', path: '/nos-formations', desc: 'Articles de formation' },
              { label: 'Espace Patients', path: '/patients', desc: 'Vue patient' },
              { label: 'Forum', path: '/forum', desc: 'Forum d\'entraide' },
              { label: 'Agenda', path: '/events', desc: 'Événements' },
              { label: 'Adhésion', path: '/adhesion', desc: 'Page d\'adhésion' },
              { label: 'Back Office Admin', path: '/admin', desc: 'Administration' },
              { label: 'Dashboard Membre', path: '/dashboard', desc: 'Espace membre' },
              { label: 'Mes Fiches', path: '/mes-fiches', desc: 'Fiches annuaire' },
              { label: 'Mon Support', path: '/mon-support', desc: 'Tickets support' },
              { label: 'Blog / Actualités', path: '/blog', desc: 'Blog public' },
            ].map((view, i) => (
              <button key={i} onClick={() => navigate(view.path)}
                className="bg-white rounded-xl border border-gray-100 p-5 text-left hover:shadow-md hover:border-[#35AFE4]/30 transition-all group"
                data-testid={`view-${i}`}>
                <div className="flex items-center gap-2 mb-2">
                  <Monitor size={14} className="text-gray-400 group-hover:text-[#35AFE4]" />
                  <h3 className="text-sm font-bold text-gray-900">{view.label}</h3>
                </div>
                <p className="text-xs text-gray-400">{view.desc}</p>
              </button>
            ))}
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
};

export default SupportTechPage;
